import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import util from 'preact-util';

import { expectedStandsForStage } from '../../lib/conventionResults';
import {
    visibleMessagesForStand, ackSummary, setLastSeen,
} from '../../lib/conventionCrewMessages';

@observer
class ConventionCrewMessages extends Component {
    constructor(props) {
        super(props);
        this.state = {
            newMessage: '',
            targetType: 'all', // 'all' | 'stand' (sekretariat mode only)
            targetStand: 1,
            viktig: 0,
            sending: false,
        };
    }

    componentDidMount() {
        this.loadMessages();
    }

    componentDidUpdate() {
        // New messages arrived (via ping/poll) while the drawer is open — they
        // are on screen, so they count as seen.
        this.markSeen();
    }

    loadMessages = async () => {
        const { conventionStore, conventionMessageStore } = this.props.stores;
        const convention = conventionStore.convention || {};
        if (convention.id) {
            await conventionMessageStore.loadForConvention(convention.id, { full: true });
        }
        this.markSeen();
    }

    markSeen = () => {
        const { conventionStore } = this.props.stores;
        const convention = conventionStore.convention || {};
        const stand = parseInt(this.props.stand, 10) || 0;
        if (convention.id) {
            setLastSeen(convention.id, stand);
        }
    }

    standCount = () => {
        const { conventionStore } = this.props.stores;
        const convention = conventionStore.convention || {};
        const stages = convention.stages || [];
        const counts = stages.map(s => expectedStandsForStage(s, convention));
        if (counts.length > 0) {
            return Math.max(...counts);
        }
        return convention.stageStands?.length || 10;
    }

    sendMessage = async () => {
        const { conventionStore, conventionMessageStore, appState } = this.props.stores;
        const convention = conventionStore.convention || {};
        const stand = parseInt(this.props.stand, 10) || 0;
        const body = (this.state.newMessage || '').trim();
        if (!body || !convention.id || this.state.sending) {
            return;
        }
        this.setState({ sending: true });
        const isStandMode = stand >= 1;
        const payload = isStandMode ? {
            convention: convention.id,
            target: 'sekretariat',
            stand: 0,
            fromStand: stand,
            priority: 0,
            body,
        } : {
            convention: convention.id,
            target: this.state.targetType === 'stand' ? 'stand' : 'all',
            stand: this.state.targetType === 'stand' ? (parseInt(this.state.targetStand, 10) || 1) : 0,
            fromStand: 0,
            priority: this.state.viktig === 1 ? 1 : 0,
            body,
        };
        const response = await conventionMessageStore.sendMessage(payload);
        if (response?.status === 201) {
            this.setState({ newMessage: '', viktig: 0, sending: false });
            // Content-free ping: bodies never travel on the public broadcast bus.
            conventionStore.broadcast('crewMessage', { priority: payload.priority });
            this.markSeen();
        } else {
            this.setState({ sending: false });
            if (typeof appState.addToast === 'function') {
                appState.addToast({
                    type: 'error',
                    message: <Text id='conventionCrewMessages.send-failed'>Could not send the message. Try again.</Text>,
                });
            }
        }
    }

    renderTargetChip = (m) => {
        if (m.fromRole === 'standplass') {
            return (
                <span class='badge bg-secondary'>
                    <Text id='conventionCrewMessages.to-sekretariat'>Til sekretariatet</Text>
                </span>
            );
        }
        if (m.target === 'stand') {
            return (
                <span class='badge bg-info text-dark'>
                    <Text id='conventionCrewMessages.stand-n' fields={{ stand: m.stand }}>Standplass {m.stand}</Text>
                </span>
            );
        }
        return (
            <span class='badge bg-secondary'>
                <Text id='conventionCrewMessages.all-stands'>Alle standplasser</Text>
            </span>
        );
    }

    renderAckChips = (m) => {
        const { acked, missing } = ackSummary(m, this.standCount());
        return (
            <div class='mt-1'>
                {acked.length > 0 && (
                    <small class='me-2 text-success'>
                        <i class='fa-solid fa-check me-1' />
                        <Text id='conventionCrewMessages.acked-by' fields={{ stands: acked.join(', ') }}>OK: {acked.join(', ')}</Text>
                    </small>
                )}
                {missing.length > 0 && (
                    <small class='text-danger'>
                        <Text id='conventionCrewMessages.missing' fields={{ stands: missing.join(', ') }}>Mangler: {missing.join(', ')}</Text>
                    </small>
                )}
            </div>
        );
    }

    render() {
        const { userStore, conventionMessageStore } = this.props.stores;
        const { user = {} } = userStore;
        const stand = parseInt(this.props.stand, 10) || 0;
        const isStandMode = stand >= 1;
        const { newMessage, targetType, targetStand, viktig, sending } = this.state;
        const messages = visibleMessagesForStand(conventionMessageStore.conventionMessages, stand);
        const standNumbers = Array.from({ length: this.standCount() }, (unused, i) => i + 1);

        return (
            <div class='h-100 d-flex flex-column bg-body text-body' style='padding-top: 3.25rem;'>
                <div class='px-3 pb-2 border-bottom'>
                    <h5 class='mb-0'>
                        <i class='fa-duotone fa-messages me-2' />
                        {isStandMode ? (
                            <Text id='conventionCrewMessages.title-stand' fields={{ stand }}>Meldinger – Standplass {stand}</Text>
                        ) : (
                            <Text id='conventionCrewMessages.title'>Meldinger</Text>
                        )}
                    </h5>
                </div>

                <div class='flex-grow-1 overflow-auto px-3 py-2' style='min-height: 0;'>
                    {messages.length === 0 && (
                        <div class='text-body-secondary text-center py-4'>
                            <Text id='conventionCrewMessages.empty'>Ingen meldinger ennå.</Text>
                        </div>
                    )}
                    {messages.map((m) => {
                        const own = parseInt(m.user, 10) === parseInt(user.id, 10);
                        const isViktig = parseInt(m.priority, 10) === 1;
                        return (
                            <div key={m.uuidv4} class={`d-flex flex-column mb-2 ${own ? 'align-items-end' : 'align-items-start'}`}>
                                <div
                                    class={`rounded p-2 ${isViktig ? 'alert-danger border border-danger' : 'bg-body-secondary'}`}
                                    style='max-width: 85%;'
                                >
                                    <div class='d-flex flex-row flex-wrap align-items-center gap-2 mb-1'>
                                        <small class='fw-bold'>
                                            {m.fromRole === 'standplass' ? (
                                                <Text id='conventionCrewMessages.from-stand' fields={{ stand: m.fromStand }}>Standplass {m.fromStand}</Text>
                                            ) : (
                                                <Text id='conventionCrewMessages.from-sekretariat'>Sekretariatet</Text>
                                            )}
                                        </small>
                                        <small class='text-body-secondary'>{m.userName}</small>
                                        {this.renderTargetChip(m)}
                                        {isViktig && (
                                            <span class='badge bg-danger'>
                                                <Text id='conventionCrewMessages.viktig'>Viktig</Text>
                                            </span>
                                        )}
                                    </div>
                                    <div style='white-space: pre-wrap;'>{m.body}</div>
                                    <small class='text-body-secondary'>{util.isoDate(m.createdDate)}</small>
                                    {!isStandMode && isViktig && this.renderAckChips(m)}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div class='border-top px-3 py-2'>
                    {!isStandMode && (
                        <div class='d-flex flex-row align-items-center gap-3 mb-2'>
                            <select
                                class='form-select form-select-sm w-auto'
                                value={targetType === 'stand' ? `stand:${targetStand}` : 'all'}
                                onInput={(e) => {
                                    const { value } = e.target;
                                    if (value === 'all') {
                                        this.setState({ targetType: 'all' });
                                    } else {
                                        this.setState({
                                            targetType: 'stand',
                                            targetStand: parseInt(value.split(':')[1], 10) || 1,
                                        });
                                    }
                                }}
                            >
                                <option value='all'>Alle standplasser</option>
                                {standNumbers.map(n => (
                                    <option key={n} value={`stand:${n}`}>Standplass {n}</option>
                                ))}
                            </select>
                            <div class='form-check'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    id='crewMsgViktig'
                                    checked={viktig === 1}
                                    onInput={(e) => this.setState({ viktig: e.target.checked ? 1 : 0 })}
                                />
                                <label class='form-check-label' for='crewMsgViktig'>
                                    <Text id='conventionCrewMessages.viktig'>Viktig</Text>
                                </label>
                            </div>
                        </div>
                    )}
                    <div class='input-group'>
                        <Localizer>
                            <textarea
                                class='form-control'
                                rows='2'
                                value={newMessage}
                                placeholder={<Text id='conventionCrewMessages.placeholder'>Write a message …</Text>}
                                onInput={(e) => this.setState({ newMessage: e.target.value })}
                            />
                        </Localizer>
                        <button
                            type='button'
                            class='btn btn-primary'
                            disabled={sending || !newMessage.trim()}
                            onClick={this.sendMessage}
                        >
                            <i class='fa-duotone fa-paper-plane-top me-1' />
                            <Text id='conventionCrewMessages.send'>Send</Text>
                        </button>
                    </div>
                    {!isStandMode && viktig === 1 && (
                        <small class='text-body-secondary'>
                            <Text id='conventionCrewMessages.viktig-help'>Viktige meldinger vises som varsel på standplassen til de er bekreftet.</Text>
                        </small>
                    )}
                </div>
            </div>
        );
    }
}

export default ConventionCrewMessages;
